import { getCBUList, GetCBUListProps } from "@/services/cbu.service";
import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { CBUFRProps, getCBUFilterRequest } from "./filter-obj";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { toaster } from "@/components/ui/toaster";
import { NavigateFunction } from "react-router-dom";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
}

export const clearFiltersHandler = async ({
  setFiltersApplied,
  setValue,
  submitFilters,
  getValues,
}: ClearFiltersHandlerProps) => {
  setValue("search", undefined);
  setValue("account_type", []);
  setFiltersApplied(false);

  submitFilters?.(getValues());
};

interface SubmitFiltersHandlerProps {
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<GetCBUListProps>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  fetchCBUs: (filters?: GetCBUListProps, showLoad?: boolean) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  fetchCBUs,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetCBUListProps> = getCBUFilterRequest(
      event as CBUFRProps,
    );

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);
    await fetchCBUs(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron aplicar los filtros.",
      });
      throw error;
    }
  }
};

interface FetchCbusHandlerProps {
  setLoadingCBU: Dispatch<SetStateAction<boolean>>;
  setCbus: Dispatch<SetStateAction<any>>;
  filters: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad: boolean;
}

export const fetchCbusHandler = async ({
  setLoadingCBU,
  setCbus,
  filters,
  navigate,
  fetchPermissions,
  showLoad,
}: FetchCbusHandlerProps) => {
  try {
    if (showLoad) setLoadingCBU(true);

    const response = await getCBUList({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setCbus(response);

    setLoadingCBU(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorObj?.message ?? "No se pudieron obtener los CBU.",
      });
    }
    throw error;
  }
};
